/**
 * ElClearFocusRestore
 *
 * Accesibilidad (WCAG 2.1 AA - 2.4.3 Focus Order):
 * Al activar el botón de borrado (×) de un componente de Element UI o del
 * componente custom `base-clear-input-button`, el foco debe volver al campo
 * correspondiente en lugar de perderse (ir al body — sucede cuando el propio
 * botón se desmonta por `v-if` al vaciarse el valor).
 *
 * Casos detectados:
 * - Botón custom `base-clear-input-button` (renderiza `<button>` con
 *   `<i class="el-icon-circle-close">`), usado tanto en el `slot="suffix"` de
 *   un `el-input` como suelto (junto a `el-date-picker` con `:clearable="false"`).
 * - Icono nativo `.el-icon-circle-close` de Element UI dentro de
 *   `.el-input__suffix` (el-input, el-select, el-date-picker individual, ...).
 * - Icono `.el-range__close-icon` del `el-date-picker` en modo rango.
 *
 * Estrategia para localizar el input al que devolver foco:
 * - Si el elemento activado está dentro de `.el-input` → `.el-input__inner`
 *   de ese contenedor (caso suffix).
 * - Si es un botón externo (hermano) → sube por los ancestros hasta encontrar
 *   un `.el-input__inner` en el subárbol (máx. 6 niveles). Cubre estructuras
 *   como `<div><label><el-form-item>...</el-form-item></label><button/></div>`
 *   donde el botón es hermano del label, no del form-item.
 * - Rango → busca `.el-range-input` en el `.el-range-editor`.
 *
 * Registro en main.js:
 *   import { initElClearFocusRestore } from '@/components/directives/ElClearFocusRestore.js'
 *   initElClearFocusRestore()
 */

function findClearSource (target) {
  // 1) Botón custom con icono de borrado (base-clear-input-button)
  const button = target.closest('button')
  if (button && button.querySelector('.el-icon-circle-close')) {
    return { source: button, kind: 'button' }
  }

  // 2) Icono nativo de Element UI en el suffix
  if (
    target.classList.contains('el-icon-circle-close') &&
    target.closest('.el-input__suffix') !== null
  ) {
    return { source: target, kind: 'suffix-icon' }
  }

  // 3) Icono de cierre del rango de date-picker
  const rangeIcon = target.closest('.el-range__close-icon')
  if (rangeIcon) {
    return { source: rangeIcon, kind: 'range' }
  }

  return null
}

function findAssociatedInput (source, kind) {
  if (kind === 'range') {
    const rangeEditor = source.closest('.el-range-editor')
    return rangeEditor ? rangeEditor.querySelector('.el-range-input') : null
  }

  // Si el origen está dentro de un .el-input (slot="suffix"), enfocamos ese
  const elInput = source.closest('.el-input')
  if (elInput) return elInput.querySelector('.el-input__inner')

  // Botón externo (hermano del componente): subimos por los ancestros hasta
  // encontrar un .el-input__inner en el subárbol. Común en date-pickers, donde
  // el .el-form-item está dentro de un <label> y el botón es hermano del label.
  // Limitamos la profundidad para no capturar inputs de campos alejados.
  let el = source.parentElement
  let depth = 0
  const MAX_DEPTH = 6
  while (el && depth < MAX_DEPTH) {
    const input = el.querySelector('.el-input__inner, .el-range-input')
    if (input) return input
    el = el.parentElement
    depth++
  }
  return null
}

function handleClearClick (e) {
  const target = e.target
  if (!(target instanceof Element)) return

  const match = findClearSource(target)
  if (!match) return

  const input = findAssociatedInput(match.source, match.kind)
  if (!input || input.disabled || input.readOnly) return

  // Diferimos para que Element UI (y el @click del botón custom) procesen
  // primero el borrado y el posible desmontaje por v-if.
  setTimeout(() => input.focus(), 0)
}

export function initElClearFocusRestore () {
  // useCapture: true — nos aseguramos de que se ejecute aunque Element UI o
  // el propio botón llamen a stopPropagation en su handler.
  document.addEventListener('click', handleClearClick, true)
}
